import { useState, useEffect } from 'react'
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import SettingTextField from "../../../components/SettingTextField"
import { Grid, Dialog } from "@mui/material";

const NAME_TAKEN_ERROR = "This feature name already exists. Try a new one.";
const UNIQUEID_TAKEN_ERROR = "This feature id already exists. Try a new one.";

const FeatureModalDialog = ({ open, onClose, featureData, mode, onSave, loading, takenFeatureName, takenFeatureId }) => {
    const [feature, setFeature] = useState(featureData);
    const [nameError, setNameError] = useState("");
    const [nameTakenError, setNameTakenError] = useState(takenFeatureName && takenFeatureName === featureData.label ? NAME_TAKEN_ERROR : "");
    const [uniqueIdError, setUniqueIdError] = useState("");
    const [uniqueIdTakenError, setUniqueIdTakenError] = useState(takenFeatureId && takenFeatureId === featureData.uid ? UNIQUEID_TAKEN_ERROR : "");
    const [isBtnDisabled, setIsBtnDisabled] = useState(true);

    useEffect(() => {
        setNameTakenError(takenFeatureName && takenFeatureName === feature.label ? NAME_TAKEN_ERROR : "");
    }, [takenFeatureName]);
    useEffect(() => {
        setUniqueIdTakenError(takenFeatureId && takenFeatureId === feature.uid ? NAME_TAKEN_ERROR : "");
    }, [takenFeatureId]);

    const handleLabelChange = value => {
        setFeature({ ...feature, label: value });
        const trimmedValue = value.trim();
        const nameError = trimmedValue === "" ? "Feature name is required" : "";
        setNameError(nameError);
        const nameTakenError = trimmedValue && trimmedValue === takenFeatureName ? NAME_TAKEN_ERROR : "";
        setNameTakenError(nameTakenError);
        const isBtnDisabled = !!nameError || !!nameTakenError || !feature.uid.trim() || !!uniqueIdTakenError;
        setIsBtnDisabled(isBtnDisabled);
    }

    const handleDescriptionChange = value => setFeature({ ...feature, description: value });

    const handleIdChange = (val) => {
        setFeature({ ...feature, uid: val });
        const trimmedValue = val.trim();
        const uniqueIdError = trimmedValue === "" ? "A unique feature id key is required" : "";
        setUniqueIdError(uniqueIdError);
        const uniqueIdTakenError = trimmedValue && trimmedValue === takenFeatureId ? UNIQUEID_TAKEN_ERROR : "";
        setUniqueIdTakenError(uniqueIdTakenError);
        const isBtnDisabled = !!uniqueIdError || !!uniqueIdTakenError || !feature.label.trim() || !!nameTakenError;
        setIsBtnDisabled(isBtnDisabled);
    }

    const handleSaveBtnClick = () => {
        const updatedFeatureFlag = { ...feature, label: feature.label.trim(), description: feature.description.trim(), uid: feature.uid.trim() };
        onSave(updatedFeatureFlag);
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            sx={styles.mainDialog} >
            <Grid p={4}>
                <DialogTitle sx={styles.confirmTitleText}>
                    {`${mode} Feature`}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-slide-description" sx={styles.confirmContentText}>
                        <Grid container spacing={2} p={0}>
                            <Grid item xs={12}>
                                <SettingTextField
                                    value={feature.label}
                                    setValue={handleLabelChange}
                                    border={"1px solid #AAA"}
                                    placeholder={""}
                                    labelStyle={styles.labelStyle}
                                    label="Feature Name"
                                    required={true}
                                    variant={"filled"}
                                    error={nameError || nameTakenError}
                                    errorStyles={styles.errorStyle}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <SettingTextField
                                    value={feature.description}
                                    setValue={handleDescriptionChange}
                                    border={"1px solid #AAA"}
                                    placeholder={""}
                                    labelStyle={styles.labelStyle}
                                    label="Description"
                                    variant={"filled"}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <SettingTextField
                                    value={feature.uid}
                                    setValue={handleIdChange}
                                    border={"1px solid #AAA"}
                                    placeholder={""}
                                    labelStyle={styles.labelStyle}
                                    label="Unique Id"
                                    variant={"filled"}
                                    error={uniqueIdError || uniqueIdTakenError}
                                    errorStyles={styles.errorStyle}
                                    required={true}
                                />
                            </Grid>
                        </Grid>
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={styles.confirmBtnDialog}>
                    <Button sx={[styles.btnText, styles.noBtnText]}
                        onClick={onClose}>
                        cancel
                    </Button>
                    <Button sx={[styles.btnText, styles.yesBtnText]}
                        disabled={isBtnDisabled}
                        onClick={handleSaveBtnClick}>
                        {loading ? "Processing..." : `${mode} Feature`}
                    </Button>
                </DialogActions>
            </Grid>
        </Dialog>
    )
}

export default FeatureModalDialog;

const styles = {
    mainDialog: {
        '& .MuiDialog-paper': {
            borderRadius: 3,
            xs: {
                width: 'calc(100% - 2rem)',
            },
            sm: {
                width: '36rem'
            },
        },
    },
    labelStyle: {
        fontSize: 14,
        fontFamily: "GilroySemiBold",
        textAlign: "left"
    },
    errorStyle: {
        textAlign: 'left',
        fontSize: '0.75rem',
    },
    confirmTitleText: {
        fontFamily: 'GilroyBold',
        textAlign: 'center',
        fontSize: '1.5rem',
        textTransform: 'capitalize'
    },
    confirmContentText: {
        fontFamily: 'GilroyMedium',
        textAlign: 'center',
    },
    confirmBtnDialog: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        padding: '1.5rem'
    },
    btnText: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textTransform: 'capitalize',
        fontFamily: "GilroySemiBold",
        height: "2.5rem",
        padding: "0 18px",
        fontSize: "14px",
        cursor: "pointer",
        border: "2px solid #000000",
        borderRadius: "8px",
        flex: 1
    },
    noBtnText: {
        color: '#000000',
        '&:hover': {
            backgroundColor: '#ffffff !important',
        },
    },
    yesBtnText: {
        backgroundColor: '#000000',
        color: '#ffffff !important',
        borderRadius: 2,
        px: 2,
        '&:hover': {
            color: '#ffffff !important',
            backgroundColor: '#000000 !important',
        },
        '&:disabled': {
            opacity: 0.5
        }
    }
}
